import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ChevronRight,
  Eye,
  ImageIcon,
  Palette,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { SignatureDialog } from "@/features/forms/components/branding-dialogs";
import { Segmented } from "@/features/settings/components/segmented";
import { FIELD_LABEL, SettingsSection } from "@/features/settings/components/settings-section";
import {
  type FirmSettings,
  type LogoAlign,
  getFirmSettings,
  updateFirmSettings,
  uploadFirmLogo,
} from "@/features/settings/firm-api";
import { useAuthedImage } from "@/hooks/use-authed-image";
import { ApiError } from "@/lib/api";

const ALIGN_OPTIONS: { value: LogoAlign; label: string; icon: LucideIcon }[] = [
  { value: "L", label: "Left", icon: AlignLeft },
  { value: "C", label: "Center", icon: AlignCenter },
  { value: "R", label: "Right", icon: AlignRight },
];

// Preset accent swatches. Colour is user data (like a colour picker), so the
// hexes live here and apply via inline style — not Tailwind arbitrary classes.
export const ACCENT_PRESETS = [
  { name: "Forest green", hex: "#2E7D4F" },
  { name: "Navy", hex: "#0D1B3E" },
  { name: "Blue", hex: "#2457E6" },
  { name: "Orange", hex: "#F28C28" },
  { name: "Charcoal", hex: "#3F4A60" },
];

type PreviewSurface = "letterhead" | "portal" | "invoice";
const PREVIEW_TABS: { value: PreviewSurface; label: string }[] = [
  { value: "letterhead", label: "Letterhead" },
  { value: "portal", label: "Portal" },
  { value: "invoice", label: "Invoice" },
];

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

/**
 * Firm branding — logo upload + render scale/alignment, an accent colour and a
 * live preview of how it lands on the letterhead/portal/invoice. The logo lands
 * on generated form attachment pages and letters (§8). Admin-only writes.
 */
export function BrandingSection({ isAdmin }: { isAdmin: boolean }) {
  const [settings, setSettings] = useState<FirmSettings | null>(null);
  const [scale, setScale] = useState(100);
  const [align, setAlign] = useState<LogoAlign>("L");
  const [saving, setSaving] = useState(false);
  const [signaturesOpen, setSignaturesOpen] = useState(false);
  const [surface, setSurface] = useState<PreviewSurface>("letterhead");
  // Accent colour has no backend field yet — local UI state so the preview
  // reflects it. TODO(api): add `accent_color` to firm settings and wire this.
  const [accent, setAccent] = useState(ACCENT_PRESETS[0]!.hex);
  const fileRef = useRef<HTMLInputElement>(null);
  const firmName = useAuthStore((s) => s.user?.firm?.name) || "Your firm";

  useEffect(() => {
    getFirmSettings()
      .then((s) => {
        setSettings(s);
        setScale(s.logoScale);
        setAlign(s.logoAlign);
      })
      .catch(() => toast.error("Couldn't load branding settings."));
  }, []);

  async function handleUpload(file: File) {
    setSaving(true);
    try {
      const updated = await uploadFirmLogo(file);
      setSettings(updated);
      setScale(updated.logoScale);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't upload the logo."));
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSaveScale() {
    setSaving(true);
    try {
      const updated = await updateFirmSettings({ logoScale: scale, logoAlign: align });
      setSettings(updated);
      toast.success("Logo settings saved");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't save logo settings."));
    } finally {
      setSaving(false);
    }
  }

  // The logo lives in private media — load it through the authenticated endpoint.
  // `logoUrl` (the stored path) changes on re-upload, busting the fetch cache.
  const logoSrc = useAuthedImage(
    settings?.logoUrl ? `/firm/settings/media/logo?v=${encodeURIComponent(settings.logoUrl)}` : null,
  );

  if (!settings) return null;

  const logoDirty = scale !== settings.logoScale || align !== settings.logoAlign;

  return (
    <SettingsSection
      title="Branding"
      icon={Palette}
      iconWellClassName="bg-warning-soft text-warning"
      subtitle="Logo, colour and signatures placed on every letter, form attachment and the client portal."
      headerAction={
        <button
          type="button"
          onClick={() => setSignaturesOpen(true)}
          className="text-info inline-flex items-center gap-1.5 text-[13px] font-semibold"
        >
          Manage signatures <ChevronRight className="size-3.5" />
        </button>
      }
      bodyClassName="grid grid-cols-1 gap-0 p-0 md:grid-cols-2"
    >
      {/* Controls */}
      <div className="flex flex-col gap-6 border-b p-6 md:border-r md:border-b-0">
        {/* Logo */}
        <div className="flex flex-col gap-2">
          <span className={FIELD_LABEL}>Logo</span>
          <div className="flex flex-wrap items-center gap-3.5">
            <div className="bg-card flex h-[88px] w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Firm logo"
                  style={{ width: `${scale}%` }}
                  className="object-contain"
                />
              ) : (
                <span className="text-muted-foreground flex flex-col items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
                  <ImageIcon className="text-info size-6" strokeWidth={1.8} />
                  {settings.logoUrl ? "Loading…" : "[Your logo]"}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/svg+xml,image/jpeg"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-dashed"
                disabled={!isAdmin || saving}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="text-info" />
                {settings.logoUrl ? "Replace logo" : "Upload logo"}
              </Button>
              <span className="text-muted-foreground text-xs">
                PNG or SVG, transparent background, at least 600&nbsp;px wide.
              </span>
            </div>
          </div>
        </div>

        {/* Scale */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="logo-scale" className={cn(FIELD_LABEL, "flex justify-between")}>
            Logo size
            <span className="text-foreground tracking-normal normal-case tabular-nums">
              {scale}%
            </span>
          </Label>
          <Slider
            id="logo-scale"
            min={1}
            max={100}
            step={1}
            value={[scale]}
            disabled={!isAdmin || !settings.logoUrl}
            onValueChange={(vals) => setScale(vals[0] ?? scale)}
          />
          <div className="text-muted-foreground flex justify-between text-[11px]">
            <span>Small</span>
            <span>Large</span>
          </div>
        </div>

        {/* Alignment */}
        <div className="flex flex-col gap-2">
          <span className={FIELD_LABEL}>Alignment on letterhead</span>
          <Segmented
            label="Logo alignment"
            value={align}
            onChange={setAlign}
            options={ALIGN_OPTIONS}
          />
        </div>

        {/* Accent colour */}
        <div className="flex flex-col gap-2">
          <span className={FIELD_LABEL}>Accent colour</span>
          <div role="group" aria-label="Accent colour" className="flex flex-wrap items-center gap-2">
            {ACCENT_PRESETS.map((preset) => {
              const selected = accent.toUpperCase() === preset.hex.toUpperCase();
              return (
                <button
                  key={preset.hex}
                  type="button"
                  aria-label={preset.name}
                  aria-pressed={selected}
                  disabled={!isAdmin}
                  onClick={() => setAccent(preset.hex)}
                  className={cn(
                    "size-8 rounded-full border-[3px] border-white transition-transform hover:scale-110",
                    selected ? "ring-2 ring-offset-0" : "ring-1 ring-border",
                  )}
                  style={{
                    backgroundColor: preset.hex,
                    ...(selected ? { boxShadow: `0 0 0 2px ${preset.hex}` } : {}),
                  }}
                />
              );
            })}
            <label className="border-input bg-card text-muted-foreground inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[13px]">
              <span className="sr-only">Custom hex colour</span>
              <span className="tabular-nums">#</span>
              <input
                type="text"
                maxLength={6}
                disabled={!isAdmin}
                value={accent.replace(/^#/, "").toUpperCase()}
                onChange={(e) => setAccent(`#${e.target.value.replace(/[^0-9a-fA-F]/g, "")}`)}
                className="w-16 bg-transparent tabular-nums uppercase outline-none"
              />
            </label>
          </div>
          <span className="text-muted-foreground text-xs">
            Used for headings and rules on letters, and buttons in the client portal.
          </span>
        </div>

        {isAdmin && logoDirty && (
          <div>
            <Button type="button" size="sm" onClick={handleSaveScale} disabled={saving}>
              {saving ? "Saving…" : "Save logo settings"}
            </Button>
          </div>
        )}
      </div>

      {/* Live preview */}
      <div className="bg-muted/40 flex flex-col gap-3.5 p-6">
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
            <Eye className="size-3.5" /> Live preview
          </span>
          <Segmented
            compact
            label="Preview surface"
            value={surface}
            onChange={setSurface}
            options={PREVIEW_TABS}
          />
        </div>

        <BrandPreview
          surface={surface}
          accent={accent}
          align={align}
          scale={scale}
          logoSrc={logoSrc}
          firmName={firmName}
        />

        <span className="text-muted-foreground text-xs">
          Updates as you change logo, size, alignment and colour. Signatures come from the Signatures
          page.
        </span>
      </div>

      <SignatureDialog open={signaturesOpen} onOpenChange={setSignaturesOpen} />
    </SettingsSection>
  );
}

const ALIGN_ITEMS: Record<LogoAlign, string> = {
  L: "items-start text-left",
  C: "items-center text-center",
  R: "items-end text-right",
};

/** Presentational preview of how the branding lands on each surface. */
function BrandPreview({
  surface,
  accent,
  align,
  scale,
  logoSrc,
  firmName,
}: {
  surface: PreviewSurface;
  accent: string;
  align: LogoAlign;
  scale: number;
  logoSrc: string | undefined;
  firmName: string;
}) {
  const logoBox = (
    <div className={cn("flex w-full flex-col gap-1", ALIGN_ITEMS[align])}>
      {logoSrc ? (
        <img
          src={logoSrc}
          alt=""
          style={{ width: `${Math.min(scale, 100)}%`, maxWidth: 120 }}
          className="h-12 object-contain"
        />
      ) : (
        <span
          className="flex h-[50px] w-[74px] items-center justify-center rounded-md border border-dashed text-[9px] font-bold tracking-wide uppercase"
          style={{ color: accent, borderColor: accent, backgroundColor: `${accent}14` }}
        >
          Logo
        </span>
      )}
      <span className="font-heading text-sm font-bold tracking-tight" style={{ color: accent }}>
        {firmName}
      </span>
    </div>
  );

  const bar = (w: string) => (
    <span className="bg-muted inline-block h-2 rounded-full" style={{ width: w }} />
  );

  if (surface === "portal") {
    return (
      <div className="overflow-hidden rounded-md border bg-white shadow-[0_1px_2px_rgba(18,26,46,0.06),0_20px_40px_-24px_rgba(18,26,46,0.35)]">
        <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ backgroundColor: accent }}>
          {logoSrc ? (
            <img src={logoSrc} alt="" className="h-5 object-contain" />
          ) : (
            <span className="font-heading text-xs font-bold">{firmName}</span>
          )}
        </div>
        <div className="flex flex-col gap-3 p-5 text-[11px] text-foreground">
          <span className="font-heading text-sm font-bold">Welcome back</span>
          <div className="flex flex-col gap-1.5">
            {bar("90%")}
            {bar("70%")}
            {bar("80%")}
          </div>
          <button
            type="button"
            tabIndex={-1}
            className="mt-1 w-fit rounded-md px-3 py-1.5 text-[11px] font-semibold text-white"
            style={{ backgroundColor: accent }}
          >
            Upload documents
          </button>
        </div>
      </div>
    );
  }

  if (surface === "invoice") {
    return (
      <div className="relative flex flex-col gap-4 overflow-hidden rounded-md border bg-white p-7 text-[11px] text-foreground shadow-[0_1px_2px_rgba(18,26,46,0.06),0_20px_40px_-24px_rgba(18,26,46,0.35)]">
        <div className="flex items-start justify-between gap-4">
          {logoBox}
          <div className="text-right">
            <span className="font-heading text-base font-bold" style={{ color: accent }}>
              INVOICE
            </span>
            <p className="text-muted-foreground mt-1 leading-relaxed">
              #0001
              <br />
              October 13, 2026
            </p>
          </div>
        </div>
        <span className="h-px" style={{ backgroundColor: `${accent}40` }} />
        <div className="flex flex-col gap-2">
          {bar("100%")}
          {bar("85%")}
          {bar("60%")}
        </div>
        <div className="mt-2 flex justify-end">
          <span className="font-heading text-sm font-bold" style={{ color: accent }}>
            Total $0.00
          </span>
        </div>
      </div>
    );
  }

  // Letterhead (default)
  return (
    <div className="relative flex flex-col gap-[18px] overflow-hidden rounded-md border bg-white px-8 py-7 text-[11px] text-foreground shadow-[0_1px_2px_rgba(18,26,46,0.06),0_20px_40px_-24px_rgba(18,26,46,0.35)]">
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[5px]"
        style={{ backgroundColor: accent }}
      />
      <div className="flex items-start justify-between gap-4">
        {logoBox}
        <span className="text-muted-foreground text-right leading-relaxed">
          [Street address]
          <br />
          [City, State ZIP]
          <br />
          [Phone] · [Email]
        </span>
      </div>
      <span className="bg-border h-px" />
      <div className="text-muted-foreground flex flex-col gap-2 leading-relaxed">
        <span className="tabular-nums">October 13, 2026</span>
        <span>
          Internal Revenue Service
          <br />
          [Service center address]
        </span>
        <span className="text-foreground font-semibold">
          Re: Request for first-time penalty abatement — [Client name], TY 2024
        </span>
        <span className="flex flex-col gap-1.5">
          <span className="inline-flex items-center gap-1.5">
            Dear Sir or Madam, {bar("40%")} {bar("30%")}
          </span>
          {bar("92%")}
          {bar("55%")}
        </span>
      </div>
      <div className="mt-1.5 flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground border-b pb-0.5 text-[10px] italic">
            [Signature]
          </span>
          <span className="font-semibold">Test CPA CAP Lastname, CPA</span>
          <span className="text-muted-foreground">Authorized representative · CAF [no.]</span>
        </div>
        <span className="font-semibold" style={{ color: accent }}>
          taxflora.com
        </span>
      </div>
    </div>
  );
}
